import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster as SonnerToaster } from 'sonner';
import { Toaster } from '@/components/ui/toaster';

import { queryClientInstance } from '@/lib/query-client';
import { OdooAuthProvider, useOdooAuth } from '@/lib/OdooAuthContext';
import { AuthProvider } from '@/lib/AuthContext';
import { RepProfileProvider } from '@/lib/RepProfileContext';
import { useCompanySetup } from '@/lib/companySetup';

import PageNotFound from '@/lib/PageNotFound';
import AppLayout from '@/components/layout/AppLayout';
import OdooLogin from '@/pages/OdooLogin';
import SplashScreen from '@/components/SplashScreen';
import InstallPrompt from '@/components/InstallPrompt';

// Pages
import Dashboard from '@/pages/Dashboard';
import Customers from '@/pages/Customers';
import CustomerDetail from '@/pages/CustomerDetail';
import NewCustomer from '@/pages/NewCustomer';
import Products from '@/pages/Products';
import StockPrint from '@/pages/StockPrint';
import ProductDetail from '@/pages/ProductDetail';
import ProductForm from '@/pages/ProductForm';
import Orders from '@/pages/Orders';
import NewOrder from '@/pages/NewOrder';
import OrderDetail from '@/pages/OrderDetail';
import DailySalesOrdersPrint from '@/pages/DailySalesOrdersPrint';
import InvoicePreview from '@/pages/InvoicePreview';
import Visits from '@/pages/Visits';
import NewVisit from '@/pages/NewVisit';
import PlanVisits from '@/pages/PlanVisits';
import ContinueVisit from '@/pages/ContinueVisit';
import VisitDetail from '@/pages/VisitDetail';
import Collections from '@/pages/Collections';
import NewCollection from '@/pages/NewCollection';
import DailyCollectionsPrint from '@/pages/DailyCollectionsPrint';
import CollectionDetail from '@/pages/CollectionDetail';
import Reports from '@/pages/Reports';
import RepMonitoring from '@/pages/RepMonitoring';
import Returns from '@/pages/Returns';
import NewReturn from '@/pages/NewReturn';
import ReturnDetail from '@/pages/ReturnDetail';
import Gifts from '@/pages/Gifts';
import NewGift from '@/pages/NewGift';
import GiftDetail from '@/pages/GiftDetail';
import Settings from '@/pages/Settings';
import OdooAdminConfig from '@/pages/OdooAdminConfig';
import AdminReps from '@/pages/AdminReps';
import AdminRepProfile from '@/pages/AdminRepProfile';
import CompanySetup from '@/pages/CompanySetup';

/**
 * Routes are gated behind a single check: do we have an Odoo session?
 * If not, show the login screen. If yes, mount the app — on SellGo's own
 * database, once the company has done its first-time setup.
 */
function GatedApp() {
  const { odooSession, isLoadingOdoo } = useOdooAuth();
  const setup = useCompanySetup(odooSession?.uid);

  if (isLoadingOdoo || setup.checking) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm font-medium">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!odooSession) return <OdooLogin />;
  if (setup.needed) return <CompanySetup company={setup.company} failed={setup.failed} onDone={setup.refresh} />;

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/new" element={<NewCustomer />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/print/stock" element={<StockPrint />} />
        <Route path="/products/new" element={<ProductForm />} />
        <Route path="/products/:id/edit" element={<ProductForm />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/new" element={<NewOrder />} />
        <Route path="/orders/print/today" element={<DailySalesOrdersPrint />} />
        <Route path="/orders/:id" element={<OrderDetail />} />
        <Route path="/orders/:id/print" element={<InvoicePreview />} />
        <Route path="/visits" element={<Visits />} />
        <Route path="/visits/new" element={<NewVisit />} />
        <Route path="/visits/plan" element={<PlanVisits />} />
        <Route path="/visits/:id/continue" element={<ContinueVisit />} />
        <Route path="/visits/:id" element={<VisitDetail />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/collections/new" element={<NewCollection />} />
        <Route path="/collections/print/today" element={<DailyCollectionsPrint />} />
        <Route path="/collections/:id" element={<CollectionDetail />} />
        <Route path="/collections/:id/print" element={<InvoicePreview />} />
        <Route path="/collections/print/receipt" element={<InvoicePreview />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/returns" element={<Returns />} />
        <Route path="/returns/new" element={<NewReturn />} />
        <Route path="/returns/:id" element={<ReturnDetail />} />
        <Route path="/gifts" element={<Gifts />} />
        <Route path="/gifts/new" element={<NewGift />} />
        <Route path="/gifts/:id" element={<GiftDetail />} />
        <Route path="/team" element={<RepMonitoring />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/admin/odoo-config" element={<OdooAdminConfig />} />
        <Route path="/admin/reps" element={<AdminReps />} />
        <Route path="/admin/reps/:id" element={<AdminRepProfile />} />
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}

export default function App() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {showSplash && <SplashScreen />}
      <OdooAuthProvider>
        <AuthProvider>
          <RepProfileProvider>
            <QueryClientProvider client={queryClientInstance}>
              <Router>
                <GatedApp />
              </Router>
              <Toaster />
              <SonnerToaster position="top-center" richColors />
              <InstallPrompt />
            </QueryClientProvider>
          </RepProfileProvider>
        </AuthProvider>
      </OdooAuthProvider>
    </>
  );
}
